/**
 * ОРГАНИЗАЦИИ, ЗАПРЕЩЁННЫЕ В РФ: звёздочка сразу после названия и строка «* Запрещена в РФ»
 * последним блоком поста (AGI Boardroom, решение Ильи 2026-11-02 — «очень важно»).
 *
 * ЗАЧЕМ ОДНА ФУНКЦИЯ И ПОЧЕМУ ЗДЕСЬ. Звёздочку и сноску ставит код, а не модель, и ставит их
 * ОДИН И ТОТ ЖЕ код на обеих сторонах приёма: у писателя (сборка черновика, до заморозки) и у
 * бэкенда (`prepare` приёма — версия писателя, правка человеком, картинка, документ, фикстуры).
 * Писатель сверяет свой `content_hash` с бэкендовым, и преобразование, сделанное сторонами
 * по-разному, — это алерт `hash_mismatch` и остановка прогона. Поэтому функция чистая, без часов
 * и без конфига, а список названий — константа выпущенного кода, одинаковая у обеих сторон.
 * Включает разметку для бренда ключ `legal.banned_orgs` его конфига (`bannedOrgMarksOn`).
 *
 * ПОЧЕМУ НЕ В `canonicalize()`. Канонизация — общая форма ВСЕХ брендов, и её повторяют подпись,
 * предотправочный валидатор и публикатор над уже утверждёнными байтами. Звёздочка внутри неё
 * поменяла бы канон у постов, уже подписанных и стоящих в слотах, — публикатор отказал бы им
 * `CONTENT_NOT_CANONICAL`. Здесь — авторский шаг ДО заморозки: подписанные байты он не трогает.
 *
 * ПОЧЕМУ ВХОД `@smm/contracts/preview`, А НЕ ОСНОВНОЙ. Звёздочка внутри адреса меняет адрес
 * (`meta.ai`, `instagram.com/…` — Telegram линкует их сам), поэтому разметке нужен детектор
 * сервера (`findUrls`, `findMentions`, `matchHashtags` — порт TDLib), а его импорт из основного
 * входа запрещён (§Хребет данных 6). Тот же выбор и по той же причине сделан у правила ссылок
 * (`link-policy.ts`): обе функции — авторские шаги писателя и приёма, в сверке после публикации
 * не участвуют.
 *
 * СВОЙСТВА, на которых держится сверка хэшей (их держат тесты свойствами, `banned-orgs.test.ts`):
 *   · идемпотентность: T(T(x)) = T(x) — переделка, правка человеком и повторная заморозка
 *     картинкой прогоняют уже размеченный текст ещё раз;
 *   · устойчивость к канонизации: T(C(T(x))) = C(T(x)) — бэкенд прогоняет разметку над
 *     канонической формой, которую прислал писатель, и обязан получить её же;
 *   · смещения — в code units UTF-16, сущности после вставки законны и не режут суррогатных пар.
 * Устойчивость держат три приёма, и каждый закрывает случай, найденный тестом свойств:
 *   1. ВСЕ решения (упоминание ли это, размечено ли, где сноска, где кончается текст)
 *      принимаются по тексту так, как его увидит канонизация (`canonViewOf`): символы, которые
 *      она вырезает, для решений прозрачны, управляющие — пробелы. Иначе писатель, видящий сырой
 *      текст модели, и бэкенд, видящий канонический, разошлись бы на одном невидимом символе;
 *   2. сущности, которые канонизация выбросит, разметка снимает сама и заранее (`canonSurvivors`):
 *      канонизация, выбросив одну, выбрасывает все, и вместе с ними пропал бы `code`, решавший,
 *      размечать ли название внутри него;
 *   3. детектор ссылок не видит собственных звёздочек разметки (`detectorZones`): иначе
 *      звёздочка меняла бы его вердикт о соседнем теге, и второй прогон снимал бы сноску первого.
 *
 * ВХОД — сущности, которые заморозка приняла бы как сущности: по `MessageEntitySchema`, в границах
 * текста, без разрезанных суррогатных пар. Бэкенд получает их из недоверенного тела запроса и
 * обязан разобрать схемой ДО разметки; содержание с иными сущностями разметка возвращает
 * нетронутым (`entitiesValid`), и отказ со своим кодом выдаёт заморозка.
 */

import { asUtf16, isCodeUnitBoundary } from '../units.ts';
import { category, findMentions, matchHashtags } from './detect/match.ts';
import { findUrls } from './detect/url.ts';
import { type MessageEntity, MessageEntitySchema } from './draft.ts';

/** Организация списка — короткий код для журнала и отказа одобрения. */
export type BannedOrgId = 'meta' | 'facebook' | 'instagram';

export interface BannedOrg {
  readonly id: BannedOrgId;
  /**
   * Формы названия в тексте поста — латиницей и кириллицей с падежными окончаниями. Совпадение —
   * только целым словом (см. `isMentionAt`).
   */
  readonly forms: readonly string[];
  /**
   * Название — ещё и обычное слово или приставка («мета», «Meta-анализ»). Его формы совпадают
   * только в записанном регистре, а дефис или косая черта сразу за ними упоминание отменяют.
   * У остальных названий другого смысла нет: они совпадают в любом регистре («в инстаграме»,
   * «INSTAGRAM»), а дефис и косая черта за ними — составное слово или перечисление, и звёздочка
   * встаёт перед ними: «Инстаграм*-аккаунт», а в «Facebook/Instagram» звёздочку получают оба
   * названия. Решение Ильи 2026-11-03.
   */
  readonly commonWord: boolean;
}

/**
 * ЧЕЙ ЭТО СПИСОК. Решение Тверского районного суда Москвы от 21.03.2022: деятельность Meta
 * Platforms Inc. по реализации продуктов — социальных сетей Facebook и Instagram — запрещена как
 * экстремистская. Отсюда три названия и их формы:
 *   · Meta — и полное «Meta Platforms» (форма длиннее идёт первой: «Meta Platforms*», а не
 *     «Meta* Platforms»);
 *   · Facebook и Instagram — продукты, названные в решении.
 * WhatsApp решением не затронут — звёздочки у него нет. Threads в решении не назван (сервис
 * появился в 2023-м) и в список не входит, пока это не решит человек: список юридический, а не
 * «всё, что выпускает Meta».
 *
 * Список — КОД, а не конфиг: его правка меняет хэш размеченных постов, и выкатываться она обязана
 * одной версией на писателя и бэкенд. Конфиг бренда разметку только включает.
 */
export const BANNED_ORGS: readonly BannedOrg[] = [
  {
    id: 'meta',
    forms: ['Meta Platforms', 'Meta', 'Мета', 'Меты', 'Мете', 'Мету', 'Метой'],
    commonWord: true,
  },
  {
    id: 'facebook',
    forms: ['Facebook', 'Фейсбук', 'Фейсбука', 'Фейсбуке', 'Фейсбуку', 'Фейсбуком'],
    commonWord: false,
  },
  {
    id: 'instagram',
    forms: ['Instagram', 'Инстаграм', 'Инстаграма', 'Инстаграме', 'Инстаграму', 'Инстаграмом'],
    commonWord: false,
  },
];

/**
 * Сноска — последним блоком поста, курсивом (прецедент — собственный пост канала Никиты, №96).
 * Одна форма на все случаи: «Запрещена» согласуется с «организацией», а не с числом названий.
 * Без суррогатных пар и без перевода строки: её длина в code units равна длине в кодпоинтах,
 * и сущность курсива считается по ней же.
 */
export const BANNED_ORG_FOOTNOTE = '* Запрещена в РФ';

/** Текст и явные сущности — то, что разметка читает и возвращает; остальное содержание ей чуждо. */
export interface BannedOrgContent {
  readonly text: string;
  readonly entities: readonly MessageEntity[];
}

/** Найденное упоминание. Смещения — в code units UTF-16 исходного текста. */
export interface BannedOrgMention {
  readonly org: BannedOrgId;
  /** Форма, как она стоит в тексте (`Метой`, `Meta Platforms`). */
  readonly form: string;
  readonly offset: number;
  readonly length: number;
  /** Сразу за названием уже стоит `*`. */
  readonly marked: boolean;
}

/**
 * Претензия страховочной проверки одобрения: упоминание без звёздочки или звёздочки без сноски.
 * Дискриминант — `kind`, а не текст: отказ одобрения называет человеку форму и место.
 */
export type BannedOrgIssue =
  | {
      readonly kind: 'unmarked';
      readonly org: BannedOrgId;
      readonly form: string;
      readonly offset: number;
    }
  | { readonly kind: 'footnote_missing' };

/* ─────────────────────────── текст глазами канонизации ─────────────────────────── */

/**
 * Символы, которые канонизация ВЫРЕЗАЕТ (шаг B, `rewriteText` в `canonical/canonicalize.ts`):
 * CR, U+2028–U+202E и три комбинирующих знака. Копия правила, а не импорт: функция шага B
 * внутренняя, и вынести её значило бы трогать канонизацию, которую эта правка обязана не
 * трогать. Расхождение копии с оригиналом ловит тест свойства T(C(T(x))) = C(T(x)) на корпусе с
 * этими символами — он и написан ради этой строки.
 */
function droppedByCanon(c: number): boolean {
  return c === 0x0d || (c >= 0x2028 && c <= 0x202e) || c === 0x030a || c === 0x0333 || c === 0x033f;
}

/** Текст, как его увидит канонизация (без обрезки краёв), и откуда взят каждый его символ. */
interface CanonView {
  readonly text: string;
  /** Индекс символа представления → индекс того же символа в исходном тексте. */
  readonly at: readonly number[];
}

/**
 * Представление строится по code units, а не по кодпоинтам: вырезаемые и заменяемые символы
 * все из BMP, а суррогатная пара переносится двумя единицами подряд и не разрывается.
 */
function canonViewOf(text: string): CanonView {
  let out = '';
  const at: number[] = [];
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    if (droppedByCanon(c)) continue;
    // Управляющий символ канонизация заменяет пробелом — и для решений он пробел.
    out += c <= 0x1f && c !== 0x0a ? ' ' : (text[i] as string);
    at.push(i);
  }
  return { text: out, at };
}

/** Позиция в исходном тексте сразу за символом `i - 1` представления; 0 — начало текста. */
function origAfter(view: CanonView, i: number): number {
  return i === 0 ? 0 : (view.at[i - 1] as number) + 1;
}

/** Индекс первого символа представления, взятого из исходной позиции не раньше `orig`. */
function viewIndexOf(view: CanonView, orig: number): number {
  let lo = 0;
  let hi = view.at.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if ((view.at[mid] as number) < orig) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/**
 * Сущности, которые канонизация СОХРАНИТ: непустые после вырезания символов и обрезки краёв
 * (шаги B–D; обрезаются только `' '` и `'\n'`, как в `rewriteText`). Остальные разметка снимает
 * сама — до любых решений.
 *
 * Зачем, а не «пусть их снимет канонизация». Канонизация, найдя хоть одну сущность, исчезнувшую
 * при обрезке, выбрасывает ВСЕ сущности текста: `canonicalizeFormattedText` передаёт
 * предупреждение `entity_dropped_by_trim` в список fatal шага E, и `normalizeEntities` возвращает
 * пустой список (поведение канонизации, трогать которую эта правка не вправе). Вместе с прочими
 * пропал бы и `code`, внутри которого название не размечалось, — и бэкенд, размечающий
 * каноническую форму писателя, поставил бы звёздочку, которой у писателя нет: `content_hash`
 * сторон разошёлся бы. Снятые здесь сущности пропали бы и так; зато остальные — не пропадут.
 */
function canonSurvivors(content: BannedOrgContent, view: CanonView): BannedOrgContent {
  const v = view.text;
  let lead = 0;
  while (lead < v.length && (v[lead] === ' ' || v[lead] === '\n')) lead += 1;
  let tail = v.length;
  while (tail > lead && (v[tail - 1] === ' ' || v[tail - 1] === '\n')) tail -= 1;
  const entities = content.entities.filter(
    (e) =>
      Math.min(viewIndexOf(view, e.offset + e.length), tail) >
      Math.max(viewIndexOf(view, e.offset), lead),
  );
  return { text: content.text, entities };
}

/* ─────────────────────────── что такое упоминание ─────────────────────────── */

/**
 * Правая граница: буква, цифра, комбинирующий знак (он принадлежит своей букве: «Metá» — не
 * «Meta») и подчёркивание (как у детектора TDLib: `Meta_AI` — имя, а не название в прозе).
 */
const WORD_CHAR_RE = /^[\p{L}\p{N}\p{M}_]$/u;
/**
 * Левая граница — то же без комбинирующего знака. Знак ПЕРЕД названием принадлежит предыдущему
 * символу, а не названию: вариант показа эмодзи (U+FE0F в «⚡️Meta»), кейкап (U+20E3 в
 * «1️⃣»). Иначе «⚡️Meta» не было бы упоминанием, хотя «🚀Meta» — упоминание, и
 * страховка одобрения (`bannedOrgIssues`) такого пропуска не увидела бы.
 */
const WORD_BEFORE_RE = /^[\p{L}\p{N}_]$/u;
const LETTER_RE = /^\p{L}$/u;
/** Дефис и его типографские двойники: «Meta-анализ» — составное слово, а не организация (`commonWord`). */
const HYPHENS: ReadonlySet<string> = new Set(['-', '\u2010', '\u2011']);
/**
 * Пробел у краёв строки сноски и у конца текста — `\s` без U+FEFF. Детектор адресов (порт TDLib)
 * считает U+FEFF символом домена, а не разделителем: в «Meta@x.com» с U+FEFF на конце почты нет,
 * и название — упоминание. Срежь разметка этот символ перед сноской — второй прогон увидел бы
 * почту `Meta@x.com`, не нашёл бы упоминания и снял бы сноску первого. Остальные символы `\s` в
 * представлении (`canonViewOf`) детектор считает разделителями, и срезать их безопасно.
 */
const BLANK_RE = /^[^\S\uFEFF]$/u;

function codePointAt(s: string, i: number): string {
  return String.fromCodePoint(s.codePointAt(i) as number);
}

function codePointBefore(s: string, i: number): string {
  const c = s.charCodeAt(i - 1);
  if (c >= 0xdc00 && c <= 0xdfff && i >= 2) {
    const h = s.charCodeAt(i - 2);
    if (h >= 0xd800 && h <= 0xdbff) return s.slice(i - 2, i);
  }
  return s[i - 1] as string;
}

function isBlankChar(ch: string | undefined): boolean {
  return ch !== undefined && BLANK_RE.test(ch);
}

interface FormEntry {
  readonly org: BannedOrgId;
  readonly form: string;
}

/** Строка таблицы форм: форма списка, она же в нижнем регистре и правило её организации. */
interface FormRule extends FormEntry {
  readonly lower: string;
  readonly commonWord: boolean;
}

/**
 * Формы по первому символу в нижнем регистре, длинная раньше короткой: «Meta Platforms» обязана
 * выиграть у «Meta» в той же позиции. Порядок равных по длине — по самой строке: выбор не зависит
 * от порядка объявления в списке.
 */
const FORMS_BY_FIRST: ReadonlyMap<string, readonly FormRule[]> = (() => {
  const all: FormRule[] = BANNED_ORGS.flatMap((o) =>
    o.forms.map((form) => ({
      org: o.id,
      form,
      lower: form.toLowerCase(),
      commonWord: o.commonWord,
    })),
  );
  all.sort((a, b) =>
    a.form.length !== b.form.length
      ? b.form.length - a.form.length
      : a.form < b.form
        ? -1
        : a.form > b.form
          ? 1
          : 0,
  );
  const map = new Map<string, FormRule[]>();
  for (const f of all) {
    const key = f.lower[0] as string;
    const list = map.get(key) ?? [];
    list.push(f);
    map.set(key, list);
  }
  return map;
})();

/**
 * Название, приклеенное справа к адресу, — не упоминание: точка с буквой за ней (`Meta.ai`,
 * `instagram.com`). Точка с пробелом или концом текста — конец фразы, упоминание. У названия —
 * обычного слова (`commonWord`) упоминание отменяют ещё дефис (`Meta-анализ`) и косая черта
 * (`Meta/llama`); у остальных за ними — составное слово или перечисление, упоминание.
 */
function gluedAfter(v: string, end: number, commonWord: boolean): boolean {
  const next = v[end];
  if (next === undefined) return false;
  if (commonWord && (HYPHENS.has(next) || next === '/')) return true;
  return next === '.' && end + 1 < v.length && LETTER_RE.test(codePointAt(v, end + 1));
}

/**
 * Название сразу за `#`, `@` или `$` — тег, имя аккаунта или тикер, а не название в прозе:
 * «#Meta», «@Meta». Правило по соседу, а не только по детектору: детектор решает, станет ли
 * `#Meta` тегом, глядя и на символ ПОСЛЕ названия (`#Meta#…` тегом не считается), а туда
 * разметка сама ставит звёздочку — решение по детектору менялось бы от собственной правки.
 */
const TAG_SIGILS: ReadonlySet<string> = new Set(['#', '@', '$']);

/**
 * Форма в позиции `start` представления — упоминание целым словом, или `null`. `form` ответа —
 * написание из текста («инстаграме»), а не из списка («Инстаграме»).
 */
function isMentionAt(v: string, start: number): (FormEntry & { readonly end: number }) | null {
  const candidates = FORMS_BY_FIRST.get((v[start] as string).toLowerCase());
  if (candidates === undefined) return null;
  if (start > 0) {
    const before = codePointBefore(v, start);
    if (WORD_BEFORE_RE.test(before) || TAG_SIGILS.has(before)) return null;
  }
  for (const f of candidates) {
    const end = start + f.form.length;
    const found = v.slice(start, end);
    if (f.commonWord ? found !== f.form : found.toLowerCase() !== f.lower) continue;
    if (end < v.length && WORD_CHAR_RE.test(codePointAt(v, end))) continue;
    if (gluedAfter(v, end, f.commonWord)) continue;
    return { org: f.org, form: found, end };
  }
  return null;
}

/** `is_word_character` TDLib: буква, цифра любого вида или `_` — как у порта `detect/match.ts`. */
function isTdWordChar(ch: string): boolean {
  const c = category(ch);
  return c === 'Letter' || c === 'DecimalNumber' || c === 'Number' || ch === '_';
}

/** Знак тела команды и имени бота: `is_alpha_digit_or_underscore` TDLib — только ASCII. */
const COMMAND_CHAR_RE = /^[0-9A-Za-z_]$/;

/**
 * Команды бота — порт `match_bot_commands` TDLib (td/telegram/MessageEntity.cpp): `/`, перед
 * которым не буква, не цифра и не `/`, `<`, `>`; тело — 1..64 знака латиницы, цифр и `_`; за ним
 * необязательное `@имя` (3..32 тех же знаков); после — снова не буква, не цифра и не `/`, `<`,
 * `>`. Сервер находит команды сам (docs/telegram.md §3), и `/Meta` — команда, а не название в
 * прозе. Кириллической формы правило не касается: её буквы телом команды не бывают.
 *
 * Порт живёт здесь, а не в `detect/`: детектор превью команд намеренно не знает (превью их не
 * рисует, `DetectedType`), и добавить их туда значило бы поменять превью ради разметки.
 */
function matchBotCommands(s: string): { readonly offset: number; readonly length: number }[] {
  const out: { offset: number; length: number }[] = [];
  let from = 0;
  for (;;) {
    const at = s.indexOf('/', from);
    if (at < 0) return out;
    from = at + 1;
    if (at > 0) {
      const prev = codePointBefore(s, at);
      if (isTdWordChar(prev) || prev === '/' || prev === '<' || prev === '>') continue;
    }
    let p = at + 1;
    while (p < s.length && COMMAND_CHAR_RE.test(s[p] as string)) p += 1;
    if (p === at + 1 || p - (at + 1) > 64) continue;
    if (s[p] === '@') {
      let q = p + 1;
      while (q < s.length && COMMAND_CHAR_RE.test(s[q] as string)) q += 1;
      if (q - (p + 1) < 3 || q - (p + 1) > 32) continue;
      p = q;
    }
    if (p < s.length) {
      const next = codePointAt(s, p);
      if (isTdWordChar(next) || next === '/' || next === '<' || next === '>') continue;
    }
    out.push({ offset: at, length: p - at });
  }
}

/**
 * Что сервер выделит сам — адреса, почта, `@имена`, `#теги`, команды бота — в координатах
 * представления.
 *
 * Детектор считается по тексту БЕЗ звёздочек, стоящих сразу за формой названия. Иначе
 * собственная звёздочка разметки меняла бы вердикт детектора о соседях, и второй прогон
 * разошёлся бы с первым: найдено на корпусе свойств — «#Meta#…» тегом не считается, а
 * «#Meta*#…» уже считается, и повторная разметка снимала сноску, поставленную первой (у команды
 * то же: «/Meta<» — не команда, «/Meta*<» — уже команда). Без этих звёздочек текст до разметки и
 * после неё для детектора один и тот же — ровно это и нужно.
 *
 * Снимается звёздочка только за названием, стоящим целым словом (`isMentionAt`), — то есть ровно
 * там, где её ставит или нашла бы разметка. Звёздочка за формой внутри чужого слова («_Meta*»)
 * остаётся: снятая, она склеила бы соседей в другой текст, чем увидит сервер, — в
 * «_Meta*example.com/Meta» детектор перестал бы видеть адрес `example.com/Meta`, и звёздочка
 * встала бы внутрь адреса.
 */
function detectorZones(v: string): (readonly [number, number])[] {
  const drop = new Set<number>();
  for (let i = 0; i < v.length; i++) {
    const hit = isMentionAt(v, i);
    if (hit !== null && v[hit.end] === '*') drop.add(hit.end);
  }
  let u = '';
  const back: number[] = [];
  for (let i = 0; i < v.length; i++) {
    if (drop.has(i)) continue;
    u += v[i] as string;
    back.push(i);
  }
  const zones: (readonly [number, number])[] = [];
  const add = (h: { readonly offset: number; readonly length: number }): void => {
    if (h.length <= 0) return;
    zones.push([back[h.offset] as number, (back[h.offset + h.length - 1] as number) + 1]);
  };
  for (const h of findUrls(u)) add(h);
  for (const h of findMentions(u)) add(h);
  for (const h of matchHashtags(u)) add(h);
  for (const h of matchBotCommands(u)) add(h);
  return zones;
}

interface ViewMention extends FormEntry {
  /** Границы в представлении. */
  readonly start: number;
  readonly end: number;
  readonly marked: boolean;
}

/**
 * Все упоминания текста. НЕ упоминание — название внутри того, что Telegram выделит сам (адрес,
 * почта, `@имя`, `#тег`, команда бота — детектор считается по представлению, то есть по тексту,
 * который увидит сервер; см. `detectorZones`), название сразу за `#`, `@` или `$`
 * (`TAG_SIGILS`), и название внутри `code`/`pre`: код показывают как есть, и звёздочка в нём
 * была бы правкой кода, а не пометкой. Кэштег названия не содержит никогда (он — заглавные
 * латинские буквы, а форм заглавными нет, и `$` перед названием отсекает `TAG_SIGILS`).
 * Ссылка-анкор (`text_link`) упоминанием остаётся: её текст читатель видит как прозу.
 *
 * `codeShielded` — есть название, которое не упоминание ТОЛЬКО из-за кода: без `code`/`pre`
 * вокруг оно было бы упоминанием. Такому тексту разметка обязана сохранить код живым
 * (`applyBannedOrgMarks`).
 */
function scan(content: BannedOrgContent): {
  view: CanonView;
  mentions: ViewMention[];
  codeShielded: boolean;
} {
  const view = canonViewOf(content.text);
  const v = view.text;
  const zones = detectorZones(v);
  const code: (readonly [number, number])[] = [];
  // Код — только тот, что переживёт канонизацию (`canonSurvivors`): решение обязано быть одним у
  // сырого текста писателя и у канонической формы, которую размечает бэкенд.
  for (const e of canonSurvivors(content, view).entities) {
    if (e.type === 'code' || e.type === 'pre') code.push([e.offset, e.offset + e.length]);
  }

  const mentions: ViewMention[] = [];
  let codeShielded = false;
  let i = 0;
  while (i < v.length) {
    const hit = isMentionAt(v, i);
    if (hit === null) {
      i += 1;
      continue;
    }
    const origStart = view.at[i] as number;
    const origEnd = origAfter(view, hit.end);
    const inZone = zones.some(([a, b]) => a < hit.end && i < b);
    const inCode = code.some(([a, b]) => a < origEnd && origStart < b);
    if (!inZone && inCode) codeShielded = true;
    if (!inZone && !inCode) {
      mentions.push({
        org: hit.org,
        form: hit.form,
        start: i,
        end: hit.end,
        marked: v[hit.end] === '*',
      });
    }
    // Найденное название съедается целиком: короткая форма внутри длинной упоминанием не бывает.
    i = hit.end;
  }
  return { view, mentions, codeShielded };
}

/* ─────────────────────────── правка текста и сущностей ─────────────────────────── */

/**
 * Сущности, на которых разметка умеет считать: массив сущностей по `MessageEntitySchema`, каждая
 * в границах текста и без разрезанной суррогатной пары, — предусловия заморозки (её схема и шаги
 * A2–A3 `checkDraftContent`). Иное содержание разметка возвращает НЕТРОНУТЫМ: сдвиг и обрезка
 * «починили» бы сущность за краем текста, а `canonSurvivors` выбросил бы негодную, — и заморозка
 * приняла бы то, что без разметки отбивает (G1: пост бренда без названий не меняется ничем).
 * Отказ со своим кодом — дело заморозки.
 */
function entitiesValid(content: BannedOrgContent): boolean {
  const { text, entities } = content as { readonly text: unknown; readonly entities: unknown };
  if (typeof text !== 'string' || !Array.isArray(entities)) return false;
  return entities.every((raw: unknown) => {
    const e = MessageEntitySchema.safeParse(raw);
    if (!e.success) return false;
    const end = e.data.offset + e.data.length;
    return (
      end <= text.length && isCodeUnitBoundary(text, e.data.offset) && isCodeUnitBoundary(text, end)
    );
  });
}

/** Содержание, которое разметка не вправе трогать (`entitiesValid`), — как пришло. */
function untouched(content: BannedOrgContent): BannedOrgContent {
  return { text: content.text, entities: content.entities };
}

/** Блочные цитаты: в расширенном сообщении занимают целые строки (`richBlockIssues`). */
const QUOTE_TYPES: ReadonlySet<string> = new Set(['blockquote', 'expandable_blockquote']);

/**
 * Вставка `*` в точки исходного текста (по возрастанию, без повторов). Сущность, которая
 * начинается в точке или после неё, сдвигается на 1; накрывающая точку — растёт на 1;
 * кончающаяся ровно в точке — остаётся: «**Meta**» даёт жирное «Meta» и простую звёздочку.
 *
 * ИСКЛЮЧЕНИЕ — ЦИТАТА (`blockquote`, `expandable_blockquote`), и это отступление от буквы §3
 * спецификации («entities ending exactly at it stay»). Цитата, кончающаяся ровно на названии, —
 * строка, которая кончается названием: оставь цитату на месте, и звёздочка встала бы за ней на
 * той же строке. В расширенном сообщении цитата занимает целые строки, и такую форму заморозка
 * отбивает (`rich_block_misaligned`), а расширенное сообщение — транспорт GeoBoost и AI Lab по
 * умолчанию: версия писателя пропала бы, правка человека и обложка получили бы отказ. Поэтому
 * цитата забирает звёздочку себе; строчные сущности живут по правилу §3.
 */
function insertMarks(content: BannedOrgContent, points: readonly number[]): BannedOrgContent {
  if (points.length === 0) return { text: content.text, entities: [...content.entities] };
  let text = '';
  let prev = 0;
  for (const p of points) {
    text += `${content.text.slice(prev, p)}*`;
    prev = p;
  }
  text += content.text.slice(prev);
  const countUpTo = (x: number, inclusive: boolean): number =>
    points.filter((p) => (inclusive ? p <= x : p < x)).length;
  const entities = content.entities.map((e) => {
    const start = e.offset + countUpTo(e.offset, true);
    const end = e.offset + e.length + countUpTo(e.offset + e.length, QUOTE_TYPES.has(e.type));
    return { ...e, offset: asUtf16(start), length: asUtf16(end - start) } as MessageEntity;
  });
  return { text, entities };
}

/** Текст до `end`; сущности за ним выпадают, накрывающие край — обрезаются по нему. */
function cutAt(content: BannedOrgContent, end: number): BannedOrgContent {
  if (end >= content.text.length) return { text: content.text, entities: [...content.entities] };
  const entities: MessageEntity[] = [];
  for (const e of content.entities) {
    if (e.offset >= end) continue;
    const eEnd = Math.min(e.offset + e.length, end);
    if (eEnd <= e.offset) continue;
    entities.push(
      eEnd === e.offset + e.length
        ? e
        : ({ ...e, length: asUtf16(eEnd - e.offset) } as MessageEntity),
    );
  }
  return { text: content.text.slice(0, end), entities };
}

/**
 * Начало блока сноски в представлении: начало пробельного прогона перед строкой сноски, или
 * `null`, если текст сноской не кончается. Строка сноски — своя строка (перед ней перевод строки
 * или начало текста; пробелы по краям строки допустимы), и стоит она последней, если не считать
 * хвостовых пробелов: «* Запрещена в РФ» посреди текста — уже не сноска, а чья-то фраза.
 */
function footnoteBlockStart(v: string): number | null {
  let end = v.length;
  while (end > 0 && isBlankChar(v[end - 1])) end -= 1;
  const start = end - BANNED_ORG_FOOTNOTE.length;
  if (start < 0 || v.slice(start, end) !== BANNED_ORG_FOOTNOTE) return null;
  let k = start;
  while (k > 0 && isBlankChar(v[k - 1]) && v[k - 1] !== '\n') k -= 1;
  if (k > 0 && v[k - 1] !== '\n') return null;
  let cut = start;
  while (cut > 0 && isBlankChar(v[cut - 1])) cut -= 1;
  return cut;
}

/** Конец текста без хвостовых пробелов — в исходных координатах, глазами канонизации. */
function blankTrimmedEnd(text: string): number {
  const view = canonViewOf(text);
  let end = view.text.length;
  while (end > 0 && isBlankChar(view.text[end - 1])) end -= 1;
  return origAfter(view, end);
}

/* ─────────────────────────── публичные функции ─────────────────────────── */

/**
 * Где начинается хвостовой блок сноски — со всеми пробелами перед ним и со всеми сносками
 * подряд, если их несколько, — в code units исходного текста; `null` — сноски в конце нет.
 * Писателю это граница авторского текста: гейт живого текста и коридор длины сноску не меряют,
 * как не меряют подпись первоисточника.
 */
export function bannedOrgFootnoteStart(text: string): number | null {
  let cut: number | null = null;
  let rest = text;
  for (;;) {
    const view = canonViewOf(rest);
    const at = footnoteBlockStart(view.text);
    if (at === null) return cut;
    cut = origAfter(view, at);
    rest = rest.slice(0, cut);
  }
}

/**
 * Снять хвостовой блок сноски вместе с пробелами перед ним. Писатель зовёт это сразу после
 * разбора ответа модели — до заголовка и подписи первоисточника: переделка получает текст уже со
 * сноской, и без снятия подпись «Источник» встала бы после неё второй.
 */
export function stripBannedOrgFootnote(content: BannedOrgContent): BannedOrgContent {
  if (!entitiesValid(content)) return untouched(content);
  const at = bannedOrgFootnoteStart(content.text);
  return at === null ? { text: content.text, entities: [...content.entities] } : cutAt(content, at);
}

/** Все упоминания текста по правилам разметки — для превью, журнала и тестов. */
export function findBannedOrgMentions(content: BannedOrgContent): BannedOrgMention[] {
  const { view, mentions } = scan(content);
  return mentions.map((m) => {
    const offset = view.at[m.start] as number;
    return {
      org: m.org,
      form: m.form,
      offset,
      length: origAfter(view, m.end) - offset,
      marked: m.marked,
    };
  });
}

/**
 * Только звёздочки, без сноски: для фрагмента, который ляжет внутрь поста целиком, — очищенного
 * мнения автора (`api/author-opinion.ts`). Размеченный заранее, он переживает разметку поста
 * без единой правки: его упоминания уже помечены, а сноску ставит разметка всего поста.
 *
 * Хвостовую сноску самого фрагмента функция снимает. Автор канала ставит её и сам (его пост 96),
 * а мнение ложится в пост абзацем, часто посреди текста: его сноска стала бы второй сноской
 * посреди поста — разметка поста снимает только хвостовую.
 */
export function markBannedOrgMentions(content: BannedOrgContent): BannedOrgContent {
  if (!entitiesValid(content)) return untouched(content);
  const live = stripBannedOrgFootnote(canonSurvivors(content, canonViewOf(content.text)));
  const { view, mentions } = scan(live);
  return insertMarks(
    live,
    mentions.filter((m) => !m.marked).map((m) => origAfter(view, m.end)),
  );
}

/**
 * Разметка поста целиком:
 *   1. снять хвостовую сноску и пробелы перед ней (её место — всегда последнее);
 *   2. поставить `*` после каждого неразмеченного упоминания;
 *   3. если в тексте есть хоть одно упоминание — срезать хвостовые пробелы и дописать
 *      `\n\n* Запрещена в РФ` с курсивом на строке сноски.
 * До всех шагов снимаются сущности, которые канонизация всё равно выбросит (`canonSurvivors`), —
 * иначе она выбросила бы вместе с ними и все остальные.
 *
 * Блок сноски пишется заново КАЖДЫМ прогоном, и тогда, когда он уже стоит в форме разметки, —
 * как велит §3: курсив, снятый человеком, возвращается. Устойчивости к канонизации это не мешает:
 * каноническая форма размеченного текста курсив сноски сохраняет, потому что сущностей, с
 * которыми канонизация выбросила бы все, в размеченном тексте нет (`canonSurvivors`).
 *
 * Текст без упоминаний и без сноски возвращается как есть, байт в байт и с теми же сущностями:
 * бренду с включённой разметкой пост без запрещённых названий не меняется ничем (G1) — в том
 * числе сущностями, которые выбросит канонизация: вместе с ними она сегодня выбрасывает все, и
 * снять их здесь значило бы поменять канон и `content_hash` поста без единой звёздочки, а то и
 * вскрыть отказ, который этот выброс прятал. Одно исключение — название, которое не упоминание
 * только потому, что стоит в коде (`codeShielded`): такому тексту код обязан пережить
 * канонизацию, иначе бэкенд, размечая каноническую форму без кода, поставил бы звёздочку,
 * которой нет у писателя.
 */
export function applyBannedOrgMarks(content: BannedOrgContent): BannedOrgContent {
  if (!entitiesValid(content)) return untouched(content);
  const live = canonSurvivors(content, canonViewOf(content.text));
  const body = stripBannedOrgFootnote(live);
  const { view, mentions, codeShielded } = scan(body);
  if (mentions.length === 0) {
    // Сноска снимается только с конца: текст той же длины — сноски не было.
    const footnoteless = body.text.length === live.text.length;
    return footnoteless && !codeShielded
      ? { text: content.text, entities: [...content.entities] }
      : body;
  }
  const marked = insertMarks(
    body,
    mentions.filter((m) => !m.marked).map((m) => origAfter(view, m.end)),
  );
  const trimmed = cutAt(marked, blankTrimmedEnd(marked.text));
  const offset = trimmed.text.length + 2;
  return {
    text: `${trimmed.text}\n\n${BANNED_ORG_FOOTNOTE}`,
    entities: [
      ...trimmed.entities,
      {
        type: 'italic',
        offset: asUtf16(offset),
        length: asUtf16(BANNED_ORG_FOOTNOTE.length),
      } as MessageEntity,
    ],
  };
}

/**
 * Страховка одобрения: упоминания без звёздочки и звёздочки без сноски. Пустой список — пост
 * размечен. Нужна потому, что разметку ставят два места, а пост, ушедший в канал без неё, — это
 * уже юридический вопрос, а не дефект оформления: одобрение бренда с включённой разметкой
 * отказывает, пока список не пуст.
 */
export function bannedOrgIssues(content: BannedOrgContent): BannedOrgIssue[] {
  const mentions = findBannedOrgMentions(content);
  const issues: BannedOrgIssue[] = [];
  for (const m of mentions) {
    if (!m.marked) issues.push({ kind: 'unmarked', org: m.org, form: m.form, offset: m.offset });
  }
  if (mentions.length > 0 && bannedOrgFootnoteStart(content.text) === null) {
    issues.push({ kind: 'footnote_missing' });
  }
  return issues;
}
